/**
 * PropertyPanel - Base class for material property editors
 * Provides common interface for rendering, getting, and setting material properties
 */
export default class PropertyPanel {
  /**
   * Create a property panel
   * @param {Object} builder - GUIBuilder class reference
   * @param {HTMLElement} container - Container element for the panel
   */
  constructor(builder, container) {
    this.builder = builder;
    this.container = container;
    this.controls = {};
  }

  /**
   * Render the property panel UI
   * Must be implemented by subclasses
   * @param {Object} initialData - Initial property values
   */
  render(initialData = {}) {
    throw new Error('render() must be implemented by subclass');
  }

  /**
   * Get current property values from the UI
   * Must be implemented by subclasses
   * @returns {Object} Current property values
   */
  getData() {
    throw new Error('getData() must be implemented by subclass');
  }

  /**
   * Update UI with property values
   * Must be implemented by subclasses
   * @param {Object} data - Property values to set
   */
  setData(data) {
    throw new Error('setData() must be implemented by subclass');
  }

  /**
   * Clear the panel UI
   */
  clear() {
    this.container.innerHTML = '';
    this.controls = {};
  }

  /**
   * Helper: Add a labeled control row
   * @param {string} label - Label text
   * @param {HTMLElement} control - Control element
   * @param {boolean} stacked - Stack vertically
   * @returns {HTMLElement} Row element
   */
  addRow(label, control, stacked = false) {
    const row = this.builder.createRow(label, control, stacked);
    this.container.appendChild(row);
    return row;
  }

  /**
   * Helper: Add a slider with label
   * @param {string} label - Label text
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @param {number} step - Step value
   * @param {number} defaultValue - Default value
   * @param {Function} onChange - Change callback
   * @returns {Object} Slider elements
   */
  addSlider(label, min, max, step, defaultValue, onChange) {
    const slider = this.builder.createSlider(min, max, defaultValue, step, onChange);
    this.addRow(label, slider.container);
    return slider;
  }

  /**
   * Helper: Add a number input with label
   * @param {string} label - Label text
   * @param {number} value - Initial value
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @param {number} step - Step value
   * @returns {HTMLInputElement} Input element
   */
  addNumberInput(label, value, min, max, step) {
    const input = this.builder.createNumberInput(value, min, max, step);
    this.addRow(label, input);
    return input;
  }

  /**
   * Helper: Add a color picker with label
   * @param {string} label - Label text
   * @param {string} value - Initial color value
   * @returns {HTMLInputElement} Color picker element
   */
  addColorPicker(label, value = '#ffffff') {
    const picker = this.builder.createColorPicker(value);
    this.addRow(label, picker);
    return picker;
  }

  /**
   * Helper: Add a select dropdown with label
   * @param {string} label - Label text
   * @param {Array} options - Array of {value, label} objects
   * @param {string} selected - Selected value
   * @returns {HTMLSelectElement} Select element
   */
  addSelect(label, options, selected = '') {
    const select = this.builder.createSelect(options, selected);
    this.addRow(label, select);
    return select;
  }

  /**
   * Helper: Add a checkbox with label
   * @param {string} label - Label text
   * @param {boolean} checked - Initial checked state
   * @returns {Object} Checkbox elements
   */
  addCheckbox(label, checked = false) {
    const checkbox = this.builder.createCheckbox(label, checked);
    this.container.appendChild(checkbox.container);
    return checkbox;
  }
}
